import { 
  AppState, 
  AppStateType, 
  StateChangeEvent, 
  AppError,
  ErrorType
} from '../types/index.js'
import { UIManager } from '../managers/UIManager.js'
import { AnalyticsManager } from '../managers/AnalyticsManager.js'
import { AREngine } from '../managers/AREngine.js'
import { AssetPreloader } from '../utils/AssetPreloader.js'
import { openExternalLink } from '../utils/linkOpener.js'
import { isIOSChrome } from '../utils/deviceDetection.js'
import { getCurrentExperienceConfig } from '../config/experience.js'
import { getClientAssetConfig } from '../config/getClientAssetConfig.js'
import { getClientIdFromURL, getClientConfig, getEnabledClients } from '../config/clients.js'
import { getAuthAccountStatus } from '../config/firebase.js'
import { CharacterConfig } from '../types/index.js'

/**
 * Central application controller managing state transitions and coordination
 * Implements event-driven architecture with state validation
 * Based on requirements 1.1, 2.1, 3.1, 4.1, 5.1, 6.1, 7.1
 */
export class ApplicationController {
  private state: AppState
  private eventListeners: Map<string, Array<(event: StateChangeEvent) => void>>
  private stateHistory: StateChangeEvent[]
  private uiManager: UIManager
  private analyticsManager: AnalyticsManager
  private arEngine: AREngine
  private assetPreloader: AssetPreloader
  private appContainer: HTMLElement
  private isStartingExperience: boolean = false
  private currentClientId: string = getClientIdFromURL() ?? 'roq'
  /** Tracks the character manually selected via CharacterSelector.
   *  When set, resolveCharacter always returns this instead of using
   *  the targetIndex from MindAR — necessary because per-character .mind
   *  files only ever contain one target (index 0), so targetIndex can't
   *  distinguish which character from the collection was chosen. */
  private selectedCharacter: CharacterConfig | null = null

  constructor() {
    this.state = this.createInitialState()
    this.eventListeners = new Map()
    this.stateHistory = []
    
    // Detect client from URL and log client-specific configuration
    const clientId = getClientIdFromURL()
    if (clientId) {
      const clientConfig = getClientConfig(clientId)
      if (clientConfig) {
        console.log(`🎯 Client detected: ${clientConfig.clientName} (${clientId})`)
      } else {
        console.warn(`⚠️ Invalid client ID: ${clientId}, falling back to ROQ`)
      }
    } else {
      console.log('🎯 No client specified, using default ROQ configuration')
    }
    
    // Initialize UI Manager
    const appContainer = document.getElementById('app')
    if (!appContainer) {
      throw new Error('App container not found')
    }
    this.appContainer = appContainer
    this.uiManager = new UIManager(appContainer)
    
    // Initialize Analytics Manager
    this.analyticsManager = new AnalyticsManager()
    
    // Initialize AR Engine with client config
    this.arEngine = new AREngine()
    
    // Initialize Asset Preloader with client-specific assets
    const clientAssetConfig = getClientAssetConfig()
    this.assetPreloader = new AssetPreloader(clientAssetConfig)
  }

  /**
   * Initialize the application controller
   * Sets up initial state and begins the application flow
   */
  async initialize(): Promise<void> {
    console.log('ROQ AR Demo initializing...')
    
    // Initialize analytics without consent initially
    await this.analyticsManager.initialize(false)
    
    // Show consent screen immediately
    this.uiManager.showConsentScreen()
    
    // Set up UI event listeners
    this.setupUIEventListeners()

    // Stop audio when the user navigates away from the AR screen
    document.addEventListener('arScreenUnmounted', () => {
      this.arEngine.stopAudio()
    })

    // Pause all AR audio when the user backgrounds the app or switches tabs
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.arEngine.pauseAudioOnly()
      }
    })

    // Stop audio when the page is being unloaded / Safari app is closed
    window.addEventListener('pagehide', () => {
      this.arEngine.stopAudio()
    })
    
    // Start real asset preloading
    this.startAssetPreloading()
    
    // Emit initial state event since we're already in LOADING state
    const initialStateEvent: StateChangeEvent = {
      previousState: AppStateType.LOADING,
      newState: AppStateType.LOADING,
      timestamp: this.state.lastStateChange,
      data: { initialized: true }
    }
    
    this.stateHistory.push(initialStateEvent)
    this.emitStateChange(initialStateEvent)
    
    console.log('Application controller initialized in LOADING state')
  }

  /**
   * Get current application state
   * @returns Current AppState object
   */
  getCurrentState(): AppState {
    return { ...this.state }
  }

  /**
   * Get current state type
   * @returns Current AppStateType enum value
   */
  getCurrentStateType(): AppStateType {
    return this.state.currentState
  }

  /**
   * Transition to a new application state with validation
   * @param newState - Target state to transition to
   * @param data - Optional data to include with state change
   */
  transitionToState(newState: AppStateType, data?: Record<string, any>): void {
    const previousState = this.state.currentState

    // No-op if already in the target state (e.g. switchClient re-entering AR_SCANNING)
    if (previousState === newState) return

    // Validate state transition
    if (!this.isValidStateTransition(previousState, newState)) {
      console.warn(`Invalid state transition from ${previousState} to ${newState}`)
      return
    }

    // Update state
    const timestamp = Date.now()
    this.state.currentState = newState
    this.state.lastStateChange = timestamp

    // Create state change event
    const stateChangeEvent: StateChangeEvent = {
      previousState,
      newState,
      timestamp,
      data
    }

    // Add to history
    this.stateHistory.push(stateChangeEvent)

    // Reset starting flag if we've successfully transitioned to PERMISSION_REQUEST or later
    if (newState !== AppStateType.LOADING) {
      this.isStartingExperience = false
    }

    // Emit state change event
    this.emitStateChange(stateChangeEvent)

    console.log(`State transition: ${previousState} → ${newState}`)
  }

  /**
   * Update specific state properties without changing the main state
   * @param updates - Partial state updates to apply
   */
  updateState(updates: Partial<AppState>): void {
    const previousState = { ...this.state }
    this.state = { ...this.state, ...updates, lastStateChange: Date.now() }

    // Emit property change event if listeners exist
    this.emitEvent('stateUpdated', { previousState, newState: this.state })
  }

  /**
   * Handle application errors with appropriate state transitions
   * @param error - Application error to handle
   */
  handleError(error: AppError): void {
    console.error('Application error:', error)
    
    this.updateState({ 
      errorMessage: error.message,
      lastStateChange: Date.now()
    })

    if (error.recoverable) {
      // For recoverable errors, emit error event but don't change state
      this.emitEvent('error', error)
    } else {
      // For non-recoverable errors, transition to error state
      this.transitionToState(AppStateType.ERROR, { error })
    }
  }

  /**
   * Register event listener for state changes
   * @param eventType - Type of event to listen for
   * @param callback - Callback function to execute
   */
  addEventListener(eventType: string, callback: (event: any) => void): void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, [])
    }
    this.eventListeners.get(eventType)!.push(callback)
  }

  /**
   * Remove event listener
   * @param eventType - Type of event to remove listener for
   * @param callback - Callback function to remove
   */
  removeEventListener(eventType: string, callback: (event: any) => void): void {
    const listeners = this.eventListeners.get(eventType)
    if (listeners) {
      const index = listeners.indexOf(callback)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }

  /**
   * Get state transition history
   * @returns Array of state change events
   */
  getStateHistory(): StateChangeEvent[] {
    return [...this.stateHistory]
  }

  /**
   * Reset application to initial state
   * Useful for error recovery or restart scenarios
   */
  reset(): void {
    this.state = this.createInitialState()
    this.stateHistory = []
    this.emitEvent('reset', { timestamp: Date.now() })
    console.log('Application controller reset')
  }

  /**
   * Replay AR experience - reset to scanning state
   * Requirements: Replay functionality
   */
  replay(): void {
    console.log('Replaying AR experience...')
    
    // Reset AR engine
    if (this.arEngine) {
      // Destroy and reinitialize AR scene
      this.arEngine.destroy()
    }
    
    // Reset state to scanning
    this.updateState({
      markerTracking: false,
      animationComplete: false
    })
    
    // Transition back to scanning state
    this.transitionToState(AppStateType.AR_SCANNING)
    
    // Hide CTAs
    this.uiManager.hideCTAButton()
    
    // Reinitialize AR scene
    this.setupARScene().catch(error => {
      console.error('Failed to reinitialize AR scene during replay:', error)
      this.handleARSetupError(error)
    })
    
    console.log('AR experience replayed - ready for new detection')
  }

  /**
   * Create initial application state
   * @returns Initial AppState object
   */
  private createInitialState(): AppState {
    return {
      currentState: AppStateType.LOADING,
      assetsLoaded: false,
      consentGiven: false,
      permissions: {
        camera: false,
        location: false
      },
      markerTracking: false,
      animationComplete: false,
      lastStateChange: Date.now()
    }
  }

  /**
   * Validate if a state transition is allowed
   * @param from - Current state
   * @param to - Target state
   * @returns True if transition is valid
   */
  private isValidStateTransition(from: AppStateType, to: AppStateType): boolean {
    // Define valid state transitions based on application flow
    const validTransitions: Record<AppStateType, AppStateType[]> = {
      [AppStateType.LOADING]: [
        AppStateType.PERMISSION_REQUEST,
        AppStateType.ERROR
      ],
      [AppStateType.PERMISSION_REQUEST]: [
        AppStateType.CLIENT_SELECTION,
        AppStateType.AR_SCANNING,
        AppStateType.ERROR,
        AppStateType.LOADING // Allow going back for retry
      ],
      [AppStateType.CLIENT_SELECTION]: [
        AppStateType.AR_SCANNING,
        AppStateType.ERROR
      ],
      [AppStateType.AR_SCANNING]: [
        AppStateType.AR_TRACKING,
        AppStateType.ERROR,
        AppStateType.PERMISSION_REQUEST // Camera permission lost
      ],
      [AppStateType.AR_TRACKING]: [
        AppStateType.AR_COMPLETE,
        AppStateType.AR_SCANNING, // Marker lost
        AppStateType.ERROR
      ],
      [AppStateType.AR_COMPLETE]: [
        AppStateType.AR_TRACKING, // Continue tracking
        AppStateType.AR_SCANNING, // Marker lost
        AppStateType.ERROR
      ],
      [AppStateType.ERROR]: [
        AppStateType.LOADING, // Retry/reset
        AppStateType.PERMISSION_REQUEST // Specific recovery
      ]
    }

    return validTransitions[from]?.includes(to) ?? false
  }

  /**
   * Emit state change event to all registered listeners
   * @param event - State change event to emit
   */
  private emitStateChange(event: StateChangeEvent): void {
    this.emitEvent('stateChange', event)
  }

  /**
   * Generic event emitter
   * @param eventType - Type of event to emit
   * @param data - Event data to send to listeners
   */
  private emitEvent(eventType: string, data: any): void {
    const listeners = this.eventListeners.get(eventType)
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in event listener for ${eventType}:`, error)
        }
      })
    }
  }

  /**
   * Set up UI event listeners
   * Requirements: 1.4, 6.4, 6.5
   * Phase 2: Added recording and experience selection listeners
   */
  private setupUIEventListeners(): void {
    // Listen for start experience event
    document.addEventListener('startExperience', () => {
      console.log('Start experience requested')
      this.handleStartExperience()
    })

    // Listen for CTA click events
    document.addEventListener('ctaClick', (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('CTA clicked:', customEvent.detail.type)
      this.handleCTAClick(
        customEvent.detail.type,
        customEvent.detail?.url,
        customEvent.detail?.openedExternally === true
      )
    })

    // Listen for animation replay events
    document.addEventListener('replayAnimation', () => {
      console.log('Replay animation requested')
      this.handleReplayAnimation()
    })

    // Listen for CTA restoration events from AR engine
    document.addEventListener('restoreCTAs', () => {
      console.log('Restoring CTAs after marker found')
      this.handleCTARestoration()
    })

    // Phase 2: Listen for recording toggle
    document.addEventListener('toggleRecording', (event: Event) => {
      const customEvent = event as CustomEvent
      this.handleToggleRecording(customEvent.detail.isRecording)
    })

    // Phase 2: Listen for recording completion
    document.addEventListener('recordingComplete', (event: Event) => {
      const customEvent = event as CustomEvent
      console.log('ApplicationController: Received recordingComplete event:', customEvent.detail)
      this.handleRecordingResult(customEvent.detail)
    })

    // Phase 2: Listen for experience selection
    document.addEventListener('experienceSelected', (event: Event) => {
      const customEvent = event as CustomEvent
      this.handleExperienceSelected(customEvent.detail.experience)
    })

    // Listen for video retake / close request
    document.addEventListener('videoRetake', () => {
      console.log('ApplicationController: Video retake/close requested')

      // Ensure video player UI is hidden
      try {
        this.uiManager.hideVideoPlayer()
      } catch (e) {
        console.warn('ApplicationController: Failed to hide video player UI', e)
      }

      // Reset AR scene to scanning state so recorded overlay does not persist
      try {
        this.replay()
      } catch (e) {
        console.error('ApplicationController: Failed to reset AR scene after video close', e)
      }

      // User can now record again for the current character/experience
    })

    // Listen for replay experience event
    document.addEventListener('replayExperience', () => {
      console.log('Replay experience requested')
      this.replay()
    })

    document.addEventListener('animationLiked', (event: Event) => {
      const customEvent = event as CustomEvent
      this.analyticsManager.fireEvent('animation_liked', {
        animation_id: customEvent.detail?.animationId || 'roq-ar-demo'
      })
    })

    document.addEventListener('animationCommented', (event: Event) => {
      const customEvent = event as CustomEvent
      this.analyticsManager.fireEvent('animation_commented', {
        animation_id: customEvent.detail?.animationId || 'roq-ar-demo',
        comment_length: customEvent.detail?.text ? String(customEvent.detail.text).length : 0
      })
    })
  }

  /**
   * Handle start experience request
   * Requirements: 1.4, 2.1, 1.1, 1.2, 1.3
   */
  private async handleStartExperience(): Promise<void> {
    // Prevent duplicate calls
    if (this.isStartingExperience) {
      console.log('Experience start already in progress, ignoring duplicate call')
      return
    }
    
    // Don't start if we've already moved past LOADING state
    if (this.state.currentState !== AppStateType.LOADING) {
      console.log(`Already past LOADING state (current: ${this.state.currentState}), ignoring start request`)
      return
    }
    
    const consentGiven = this.uiManager.isConsentGiven()
    const assetsReady = this.assetPreloader.isReady()
    
    console.log(`Start experience check - Consent: ${consentGiven}, Assets: ${assetsReady}`)
    
    if (consentGiven && assetsReady) {
      this.isStartingExperience = true
      console.log('Starting AR experience...')
      
      try {
        // Update analytics consent and fire consent event
        await this.analyticsManager.setConsent(true)
        await this.analyticsManager.fireConsentGiven()
        
        this.updateState({ consentGiven: true })
        this.transitionToState(AppStateType.PERMISSION_REQUEST)
        this.requestAllPermissions()
      } catch (error) {
        console.error('Error starting experience:', error)
        this.isStartingExperience = false
        throw error
      }
    } else {
      console.log('Cannot start experience - missing consent or assets not ready')
      
      if (!consentGiven) {
        console.log('Consent not given')
      }
      
      if (!assetsReady) {
        console.log('Assets not ready, current progress:', this.assetPreloader.getLoadingProgress() + '%')
      }
    }
  }

  /**
   * Handle CTA click events
   * Requirements: 6.4, 6.5
   * Opens link synchronously (before any await) to preserve user-gesture context on iOS.
   */
  private async handleCTAClick(type: '2D' | '3D', urlOverride?: string, openedExternally: boolean = false): Promise<void> {
    console.log(`CTA ${type} clicked`)

    // Open link immediately, before any await, to avoid iOS popup blocking.
    const targetUrl = urlOverride ?? getCurrentExperienceConfig().ctaUrl
    if (!openedExternally) {
      openExternalLink(targetUrl)
    }

    // Fire analytics after navigation (async is fine here).
    if (type === '2D') {
      await this.analyticsManager.fireCTAClick2D()
    } else {
      await this.analyticsManager.fireCTAClick3D()
    }
  }

  /**
   * Request all required permissions (camera and location)
   * Requirements: 2.1, 2.2, 2.3, 2.4, 2.5
   */
  private async requestAllPermissions(): Promise<void> {
    console.log('Requesting all required permissions...')
    
    try {
      // Check browser compatibility first
      const compatibilityCheck = await this.checkBrowserCompatibility()
      if (!compatibilityCheck.isCompatible) {
        console.error('Browser compatibility check failed:', compatibilityCheck.issues)
        this.handleBrowserCompatibilityError(compatibilityCheck)
        return
      }
      
      // Request camera permission first with comprehensive error handling
      let cameraGranted = false
      try {
        cameraGranted = await this.requestCameraPermission()
      } catch (error) {
        console.error('Camera permission request failed with exception:', error)
        await this.handleCameraPermissionError(error)
        return
      }
      
      // If camera permission denied, show error immediately
      if (!cameraGranted) {
        console.log('Camera permission denied - showing error screen')
        await this.handleCameraPermissionDenied()
        return
      }
      
      // Request location permission only if camera was granted
      let locationGranted = false
      try {
        locationGranted = await this.requestLocationPermission()
      } catch (error) {
        console.error('Location permission request failed with exception:', error)
        await this.handleLocationPermissionError(error)
        return
      }
      
      // If location permission denied, show error immediately
      if (!locationGranted) {
        console.log('Location permission denied - showing error screen')
        await this.handleLocationPermissionDenied()
        return
      }
      
      // Collect location data now that permission is granted
      try {
        await this.analyticsManager.collectLocationDataWithPermission()
        console.log('Location data collected for analytics')
      } catch (error) {
        console.warn('Failed to collect location data for analytics:', error)
        // Non-critical error - continue with AR setup
      }
      
      // All permissions granted - proceed with AR setup
      console.log('All permissions granted')
      await this.analyticsManager.firePermissionsGranted()
      
      // Update state with successful permissions
      this.updateState({ 
        permissions: {
          camera: true,
          location: true
        }
      })
      

      // Give the OS/browser camera stack time to fully release the
      // permission-check stream before MindAR opens its own — on some
      // Android/Chrome devices, re-acquiring the camera too quickly after
      // stop() returns a track that reports "live" but produces black frames.
      await new Promise(resolve => setTimeout(resolve, 300))

      this.transitionToState(AppStateType.AR_SCANNING)

      // Clear the consent screen and show AR interface
      this.uiManager.showCameraView()

      // Setup AR scene with comprehensive error handling
      try {
        await this.setupARScene()
      } catch (error) {
        console.error('AR scene setup failed after permissions granted:', error)
        this.handleARSetupError(error)
      }
      //this.transitionToState(AppStateType.AR_SCANNING)

      // Clear the consent screen and show AR interface
      //this.uiManager.showCameraView()

      // Setup AR scene with comprehensive error handling
      // try {
      //   await this.setupARScene()
      // } catch (error) {
      //   console.error('AR scene setup failed after permissions granted:', error)
      //   this.handleARSetupError(error)
      // }
      
    } catch (error) {
      console.error('Unexpected error during permission request flow:', error)
      this.handleError({
        type: ErrorType.UNKNOWN_ERROR,
        message: 'Unexpected error during permission request',
        timestamp: Date.now(),
        recoverable: true,
        details: { 
          error: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined
        }
      })
    }
  }

  /**
   * Request camera permission with comprehensive error handling
   * @returns Promise<boolean> - true if granted, false if denied
   * @throws Error for unexpected failures that need special handling
   */
  private async requestCameraPermission(): Promise<boolean> {
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser')
      }
      
      console.log('Requesting camera permission...')
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment', // Prefer back camera for AR
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      })
      
      console.log('Camera permission granted')
      
      // Stop the stream as we'll reinitialize it with MindAR
      stream.getTracks().forEach(track => {
        track.stop()
        console.log(`Stopped camera track: ${track.kind}`)
      })
      
      return true
      
    } catch (error) {
      console.error('Camera permission request failed:', error)
      
      // Handle specific camera errors
      if (error instanceof Error) {
        switch (error.name) {
          case 'NotAllowedError':
            console.log('Camera permission explicitly denied by user')
            return false
          case 'NotFoundError':
            console.error('No camera device found')
            throw new Error('No camera device available')
          case 'NotReadableError':
            console.error('Camera is already in use by another application')
            throw new Error('Camera is already in use')
          case 'OverconstrainedError':
            console.error('Camera constraints cannot be satisfied')
            // Try again with basic constraints
            try {
              const basicStream = await navigator.mediaDevices.getUserMedia({ video: true })
              basicStream.getTracks().forEach(track => track.stop())
              console.log('Camera permission granted with basic constraints')
              return true
            } catch (basicError) {
              console.error('Camera permission failed even with basic constraints:', basicError)
              return false
            }
          case 'NotSupportedError':
            console.error('Camera not supported in this context (HTTPS required)')
            throw new Error('Camera requires secure context (HTTPS)')
          case 'AbortError':
            console.error('Camera permission request was aborted')
            return false
          default:
            console.error('Unknown camera error:', error.message)
            throw error
        }
      }
      
      // For non-Error objects, treat as permission denied
      return false
    }
  }

  /**
   * Request location permission with iOS Safari compatibility
   * @returns Promise<boolean> - true if granted, false if denied
   * @throws Error for unexpected failures that need special handling
   */
  private async requestLocationPermission(): Promise<boolean> {
    if (!navigator.geolocation) {
      console.error('Geolocation API not supported in this browser')
      throw new Error('Geolocation not supported')
    }

    // Check if we're on iOS Safari
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    const isSafari = /Safari/.test(navigator.userAgent) && !isIOSChrome() && !/Chrome/.test(navigator.userAgent)
    
    console.log('Requesting location permission...', { isIOS, isSafari })

    // Try Permissions API first (if available)
    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        console.log('Permissions API result:', permission.state)
        
        if (permission.state === 'denied') {
          console.log('Location permission already denied via Permissions API')
          return false
        }
        
        if (permission.state === 'granted') {
          console.log('Location permission already granted via Permissions API')
          return true
        }
      } catch (error) {
        console.log('Permissions API not available or failed:', error)
      }
    }

    // For iOS Safari, use a more compatible approach
    return new Promise((resolve, reject) => {
      const timeoutDuration = isIOS && isSafari ? 20000 : 15000 // Longer timeout for iOS
      
      const timeoutId = setTimeout(() => {
        console.error('Location permission request timed out')
        if (isIOS && isSafari) {
          // On iOS Safari, timeout often means permission was silently denied
          console.log('iOS Safari timeout - treating as permission denied')
          resolve(false)
        } else {
          reject(new Error('Location permission request timed out'))
        }
      }, timeoutDuration)
      
      const options = {
        timeout: isIOS && isSafari ? 18000 : 12000, // Longer timeout for iOS
        maximumAge: 300000, // 5 minutes
        enableHighAccuracy: false // Use coarse location for better compatibility
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          clearTimeout(timeoutId)
          console.log('Location permission granted')
          console.log(`Location accuracy: ${position.coords.accuracy}m`)
          resolve(true)
        },
        (error) => {
          clearTimeout(timeoutId)
          console.error('Location permission error:', error)
          
          // Handle specific geolocation errors
          switch (error.code) {
            case error.PERMISSION_DENIED:
              console.log('Location permission explicitly denied by user')
              resolve(false)
              break
            case error.POSITION_UNAVAILABLE:
              if (isIOS && isSafari) {
                // On iOS Safari, this sometimes means permission was denied
                console.log('iOS Safari: Position unavailable - treating as permission denied')
                resolve(false)
              } else {
                console.error('Location information unavailable')
                reject(new Error('Location service unavailable'))
              }
              break
            case error.TIMEOUT:
              if (isIOS && isSafari) {
                // On iOS Safari, timeout often means permission was denied
                console.log('iOS Safari: Timeout - treating as permission denied')
                resolve(false)
              } else {
                console.error('Location request timed out')
                reject(new Error('Location request timed out'))
              }
              break
            default:
              console.error('Unknown location error:', error.message)
              if (isIOS && isSafari) {
                // On iOS Safari, unknown errors often mean permission issues
                console.log('iOS Safari: Unknown error - treating as permission denied')
                resolve(false)
              } else {
                reject(new Error(`Location error: ${error.message}`))
              }
              break
          }
        },
        options
      )
    })
  }

  /**
   * Switch to a different client from the Discover slider.
   * Destroys the current AR scene and restarts with the selected client.
   */
  async switchClient(clientId: string): Promise<void> {
    if (clientId === this.currentClientId) return
    console.log(`🔀 Switching client: ${this.currentClientId} → ${clientId}`)
    this.currentClientId = clientId

    if (this.arEngine) {
      this.arEngine.destroy()
    }
    this.arEngine = new AREngine(clientId)

    // Reset scanning state and restart AR
    this.updateState({ markerTracking: false, animationComplete: false })
    this.transitionToState(AppStateType.AR_SCANNING)
    this.uiManager.showCameraView()
    this.uiManager.hideCharacterSelectButton() // remove stale button from previous client
    this.selectedCharacter = null // clear any per-character selection from the old client

    try {
      await this.setupARScene()
    } catch (error) {
      console.error('AR scene setup failed after client switch:', error)
      this.handleARSetupError(error)
    }
  }

  /**
   * Set up AR scene and show guidance
   * Requirements: 3.1, 3.2, 3.3
   */
  private async setupARScene(): Promise<void> {
    try {
      console.log('Setting up AR scene...')
      
      // Initialize AR engine with A-Frame scene
      await this.arEngine.initializeScene(this.appContainer)
      // TEMP DEBUG: inspect MindAR's actual camera video element
      setTimeout(() => {
        const videos = document.querySelectorAll('video')
        console.log('🔍 DEBUG: total <video> elements in DOM:', videos.length)
        videos.forEach((v, i) => {
          console.log(`🔍 video[${i}]`, {
            id: v.id,
            readyState: v.readyState,
            videoWidth: v.videoWidth,
            videoHeight: v.videoHeight,
            paused: v.paused,
            srcObject: !!v.srcObject,
            currentTime: v.currentTime,
            style: v.style.cssText,
            parent: v.parentElement?.tagName,
          })
        })

        const canvases = document.querySelectorAll('canvas')
        console.log('🔍 DEBUG: total <canvas> elements in DOM:', canvases.length)
        canvases.forEach((c, i) => {
          console.log(`🔍 canvas[${i}]`, {
            width: c.width,
            height: c.height,
            clientWidth: c.clientWidth,
            clientHeight: c.clientHeight,
            style: c.style.cssText,
            parent: c.parentElement?.tagName,
          })
          // try {
          //   const gl = c.getContext('webgl') || c.getContext('webgl2')
          //   if (gl) {
          //   const pixels = new Uint8Array(4)
          //   const w = gl.drawingBufferWidth
          //   const h = gl.drawingBufferHeight
          //   // sample center pixel
          //   gl.readPixels(Math.floor(w/2), Math.floor(h/2), 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixels)
          //   console.log(`🔍 canvas[${i}] center pixel RGBA:`, Array.from(pixels), `drawingBuffer: ${w}x${h}`)
          //   } else {
          //     console.log(`🔍 canvas[${i}] could not get webgl context (already owned by A-Frame renderer)`)
          //   }
          // } catch (e : any) {
          //     console.log(`🔍 canvas[${i}] readPixels failed:`, e?.message ?? String(e))
          // }
        })
      }, 2000)
      // Set up marker event callbacks
      this.arEngine.onMarkerFound((targetIndex: number) => {
        this.handleMarkerDetected(targetIndex)
      })
      
      this.arEngine.onMarkerLost(() => {
        this.handleMarkerLost()
      })
      
      // Note: Guidance text is now shown automatically by AREngine timeout

      // Wire up the Discover button to the client slider
      const sliderClients = getEnabledClients()
        .filter(c => ['roq', 'freedom-fighters', 'pioneers', 'aana', 'rockardemo'].includes(c.clientId))
        .sort((a, b) => {
          const order = ['roq', 'freedom-fighters', 'pioneers', 'aana', 'rockardemo']
          return order.indexOf(a.clientId) - order.indexOf(b.clientId)
        })
      this.uiManager.setOnClientSwitch(sliderClients, this.currentClientId, async (clientId) => {
        await this.switchClient(clientId)
      })

      // For multi-character collections, show "Choose Character" button so the
      // user can manually select which pioneer / character to load and play.
      // Use this.currentClientId (not getClientIdFromURL) because switchClient
      // changes the active client without changing the browser URL.
      const clientConfig = getClientConfig(this.currentClientId)
      if (clientConfig?.characters?.length) {
        this.uiManager.setOnCharacterSelect(async (character: CharacterConfig) => {
          console.log(`Character manually selected: ${character.name}`)
          // Store the selection so resolveCharacter returns the right character
          // when MindAR fires targetIndex 0 from the per-character .mind file.
          this.selectedCharacter = character
          try {
            // If the character has its own .mind file, tear down the current
            // AR scene and reinitialise MindAR pointing at that single-target
            // file.  This ensures the correct pin is tracked.
            if (character.mindFile) {
              console.log(`Reinitialising AR scene with per-character .mind: ${character.mindFile}`)
              this.arEngine.destroy()
              this.arEngine = new AREngine(this.currentClientId, character.mindFile)
              this.updateState({ markerTracking: false, animationComplete: false })
              this.transitionToState(AppStateType.AR_SCANNING)

              // Re-wire callbacks on the new engine
              this.arEngine.onMarkerFound((targetIndex: number) => {
                this.handleMarkerDetected(targetIndex)
              })
              this.arEngine.onMarkerLost(() => {
                this.handleMarkerLost()
              })
              this.arEngine.onAnimationComplete(() => {
                this.handleAnimationComplete()
              })

              await this.arEngine.initializeScene(this.appContainer)
            }

            // Model is pre-loaded; animation will start when MindAR detects
            // the physical target.  handleMarkerDetected() calls playAnimation()
            // once targetFound fires, so we must NOT play here.
            await this.arEngine.loadModel(character)
            // (Do NOT call playAnimation or transition to AR_TRACKING here —
            //  that happens in handleMarkerDetected when the target is found.)

            // Re-show the Choose Character button so user can switch again.
            // Also reset showCharacterSelector to false so the next button
            // click triggers a fresh state change (otherwise the ScanScreen
            // useEffect won't fire if the flag is already true).
            const cfg = getClientConfig(this.currentClientId)
            if (cfg?.characters?.length) {
              this.uiManager.hideCharacterSelector()
              this.uiManager.showCharacterSelectButton(() => {
                this.uiManager.showCharacterSelector(cfg.characters!)
              })
            }
          } catch (err) {
            console.error('Failed to load selected character:', err)
          }
        })
        this.uiManager.showCharacterSelectButton(() => {
          const chars = clientConfig.characters!
          this.uiManager.showCharacterSelector(chars)
        })
      }

      console.log('AR scene initialized successfully')
      
    } catch (error) {
      console.error('Failed to setup AR scene:', error)
      this.handleError({
        type: ErrorType.AR_INITIALIZATION_FAILED,
        message: 'Failed to initialize AR scene',
        timestamp: Date.now(),
        recoverable: true
      })
    }
  }

  /**
   * Resolve the CharacterConfig for a given targetIndex from the current client.
   * Returns undefined for single-character clients (ROQ, RockABlock).
   */
  private resolveCharacter(targetIndex: number): CharacterConfig | undefined {
    // When the user has manually selected a character via the CharacterSelector
    // the AREngine is pointing at a per-character .mind file that only has one
    // target.  MindAR will always report targetIndex 0, so we can't use the
    // index to identify the character — return the stored selection instead.
    if (this.selectedCharacter) {
      return this.selectedCharacter
    }

    const clientId = getClientIdFromURL()
    if (!clientId) return undefined
    const clientConfig = getClientConfig(clientId)
    if (!clientConfig?.characters?.length) return undefined
    // Prefer array-order mapping: many compiled .mind files index
    // targets in the same order as characters were exported. Use
    // characters[targetIndex] when present so pins match printed order.
    if (clientConfig.characters[targetIndex]) {
      return clientConfig.characters[targetIndex]
    }
    // Fallback: use explicit targetIndex field if present
    return clientConfig.characters.find(c => c.targetIndex === targetIndex)
  }

  /**
   * Handle MindAR marker detection success
   * Requirements: 4.1, 4.2 - Fire analytics event for successful scan and manage content
   */
  async handleMarkerDetected(targetIndex = 0): Promise<void> {
    // Guard: Prevent duplicate processing if already tracking
    if (this.state.markerTracking && this.state.currentState !== AppStateType.AR_SCANNING) {
      return // Already processing this detection
    }

    // Resolve character for multi-target collections
    const character = this.resolveCharacter(targetIndex)
    if (character) {
      console.log(`Character detected: ${character.name} (targetIndex: ${targetIndex})`)
    } else {
      console.log('ROQ marker detected via MindAR!')
    }

    // Fire analytics event for successful scan (only if this is a new detection)
    if (this.state.currentState === AppStateType.AR_SCANNING) {
      await this.analyticsManager.fireRoqScanSuccess()
    }

    // Ensure animation complete callback is always set up
    this.arEngine.onAnimationComplete(() => {
      this.handleAnimationComplete()
    })

    // Update animation state management for MindAR target found event
    this.arEngine.updateAnimationStateForMindAR('target-found')

    // Load 3D content only on first detection or if AR engine is not ready
    if (!this.arEngine.isReady()) {
      try {
        await this.arEngine.loadModel(character)

        // Start animation for first time with MindAR tracking
        this.arEngine.playAnimation()

      } catch (error) {
        console.error('Failed to load AR content:', error)
        this.handleError({
          type: ErrorType.ASSET_LOAD_FAILED,
          message: 'Failed to load AR content',
          timestamp: Date.now(),
          recoverable: true
        })
        return
      }
    } else {
      // Model already loaded, handle animation state for MindAR tracking
      try {
        await this.arEngine.loadModel(character) // will reload if character changed
        
        // Get current animation state for MindAR tracking decision
        const animationState = this.arEngine.getAnimationState()
        console.log(`MindAR target found - current animation state: ${animationState}`)
        
        if (animationState === 'stopped') {
          // Start animation if it hasn't been started yet
          console.log('Starting animation for MindAR tracking')
          this.arEngine.playAnimation()
        } else if (animationState === 'paused') {
          // Resume animation if it was paused due to MindAR target loss
          console.log('Resuming animation paused by MindAR target loss')
          this.arEngine.resumeAnimation()
        } else if (animationState === 'completed') {
          // Animation was already completed, restore CTAs for MindAR tracking
          console.log('Animation was completed - restoring CTAs for MindAR tracking')
          
          // Update state to completed first
          this.updateState({ 
            markerTracking: true,
            animationComplete: true 
          })
          this.transitionToState(AppStateType.AR_COMPLETE)
          
          // Add and show CTAs for MindAR tracking
          this.arEngine.addCTA3D()
          this.arEngine.showCTA3D()
          this.uiManager.showCTAButton()
          
          // Show replay button since animation is complete
          this.uiManager.showReplayButton()
        }
        
      } catch (error) {
        console.error('Failed to handle MindAR tracking animation state:', error)
      }
    }
    
    // Update state - MindAR tracking is now active
    this.updateState({ markerTracking: true })
    
    // Transition to appropriate state based on animation state
    const animationState = this.arEngine.getAnimationState()
    if (animationState === 'completed') {
      // CTAs will be restored by MindAR animation state management
      console.log('Animation completed - MindAR tracking will restore CTAs')
    } else {
      this.transitionToState(AppStateType.AR_TRACKING)
    }
    
    console.log('MindAR marker detection handling completed')
  }

  /**
   * Handle animation completion
   * Requirements: 6.1, 6.2 - Show CTAs when animation completes
   */
  handleAnimationComplete(): void {
    console.log('Animation completed, showing CTAs')
    
    // Update state
    this.updateState({ animationComplete: true })
    this.transitionToState(AppStateType.AR_COMPLETE)
    
    // Add and show 3D CTA
    this.arEngine.addCTA3D()
    this.arEngine.showCTA3D()
    
    // Show 2D CTA overlay
    this.uiManager.showCTAButton()
    
    // Show replay button
    this.uiManager.showReplayButton()
    this.uiManager.showSocialInteractionPanel(this.currentClientId)
  }

  /**
   * Handle animation replay request
   */
  handleReplayAnimation(): void {
    console.log('Handling animation replay...')
    
    // Hide replay button
    this.uiManager.hideReplayButton()
    this.uiManager.hideSocialInteractionPanel()
    
    // Reset animation in AR engine
    this.arEngine.resetAnimation()
    
    // Hide CTAs
    this.arEngine.hideCTA3D()
    this.uiManager.hideCTAButton()
    this.uiManager.hideSocialInteractionPanel()
    
    // Reset animation complete callback and play animation again
    this.arEngine.onAnimationComplete(() => {
      this.handleAnimationComplete()
    })
    
    // Play animation again if marker is still detected
    if (this.state.markerTracking) {
      this.arEngine.playAnimation()
    }
  }

  /**
   * Handle MindAR marker lost
   * Requirements: 5.1, 5.2 - Pause animation and hide CTAs when MindAR target is lost
   */
  handleMarkerLost(): void {
    console.log('ROQ marker lost via MindAR')
    
    // Update animation state management for MindAR target lost event
    this.arEngine.updateAnimationStateForMindAR('target-lost')
    
    // Update application state
    this.updateState({ markerTracking: false })
    
    // Pause animation with MindAR-specific state management (handled by updateAnimationStateForMindAR)
    // this.arEngine.pauseAnimation() - now handled by updateAnimationStateForMindAR
    
    // Hide CTAs and social panel during MindAR tracking interruption
    this.uiManager.hideCTAButton()
    this.uiManager.hideSocialInteractionPanel()
    
    // Transition to scanning state when MindAR target is lost
    if (this.state.currentState === AppStateType.AR_COMPLETE || 
        this.state.currentState === AppStateType.AR_TRACKING) {
      this.transitionToState(AppStateType.AR_SCANNING)
    }
    
    console.log('MindAR marker lost handling completed')
    // Note: Guidance text display is handled by ROQ demo controller timeout
  }

  /**
   * Handle CTA restoration after marker is found again
   * Requirements: 5.4, 5.5 - Restore CTAs when marker returns if animation was complete
   */
  handleCTARestoration(): void {
    console.log('Restoring CTAs after marker found')
    
    // Update state back to AR_COMPLETE
    this.updateState({ 
      markerTracking: true,
      animationComplete: true 
    })
    this.transitionToState(AppStateType.AR_COMPLETE)
    
    // Show 2D CTA overlay
    this.uiManager.showCTAButton()
    this.uiManager.showSocialInteractionPanel(this.currentClientId)
    
    console.log('CTAs restored successfully')
  }



  /**
   * Start real asset preloading with progress tracking
   * Requirements: 1.5, 1.1, 1.2, 1.3
   */
  private async startAssetPreloading(): Promise<void> {
    try {
      console.log('Starting asset preloading...')
      
      await this.assetPreloader.preloadAssets(
        // Progress callback
        (progress: number) => {
          console.log(`Asset loading progress: ${progress}%`)
          this.uiManager.updateLoadingProgress(progress)
        },
        
        // Complete callback
        () => {
          console.log('All assets loaded successfully')
          this.updateState({ assetsLoaded: true })
          
          // Automatically start experience if consent is already given
          const consentGiven = this.uiManager.isConsentGiven()
          if (consentGiven) {
            console.log('Assets loaded and consent given - automatically starting experience')
            // Small delay to ensure UI updates properly
            setTimeout(() => {
              this.handleStartExperience()
            }, 100)
          }
        },
        
        // Error callback
        (error) => {
          console.error('Asset loading error:', error)
          this.handleAssetLoadingError(error)
        }
      )
      
    } catch (error) {
      console.error('Failed to start asset preloading:', error)
      this.handleError({
        type: ErrorType.ASSET_LOAD_FAILED,
        message: 'Failed to start asset preloading',
        timestamp: Date.now(),
        recoverable: true,
        details: { error: error instanceof Error ? error.message : 'Unknown error' }
      })
    }
  }

  /**
   * Handle asset loading errors with appropriate recovery strategies
   * Requirements: 1.5
   */
  private handleAssetLoadingError(error: any): void {
    console.error('Asset loading failed:', error)
    
    // Create structured error
    const assetError: AppError = {
      type: ErrorType.ASSET_LOAD_FAILED,
      message: `Asset loading failed: ${error.message}`,
      timestamp: Date.now(),
      recoverable: true,
      details: { 
        assetId: error.assetId,
        errorType: error.type 
      }
    }
    
    const loadedAssets = this.assetPreloader.getLoadingProgress()

    // If the critical assets (AR libraries + image target) are already loaded
    // — indicated by >= 50 % progress — treat any failure as non-blocking and
    // continue rather than leaving the consent screen permanently frozen.
    if (loadedAssets >= 50) {
      console.warn(`Asset loading error but ${loadedAssets}% loaded — continuing with available assets`)
      this.updateState({ assetsLoaded: true })
      return
    }
    
    // For insufficient assets, show error
    this.handleError(assetError)
  }

  /**
   * Get asset preloader instance for debugging
   * @returns AssetPreloader instance
   */
  getAssetPreloader(): AssetPreloader {
    return this.assetPreloader
  }

  // Debug helpers callable from browser console: window.app.debugTriggerFound() / window.app.debugPlayLoadedAnimation()
  debugTriggerFound(targetIndex = 0): void {
    console.log('DEBUG: forcing marker found event for targetIndex', targetIndex)
    this.handleMarkerDetected(targetIndex).catch(err => console.error('DEBUG: force marker handling failed', err))
  }

  debugPlayLoadedAnimation(): void {
    console.log('DEBUG: attempting to play loaded animation')
    try {
      this.arEngine.playAnimation()
    } catch (err) {
      console.error('DEBUG: playAnimation failed', err)
    }
  }

  /**
   * Debug method to test analytics connection
   * Can be called from browser console: window.app.testAnalytics()
   */
  async testAnalytics(): Promise<void> {
    console.log('🔍 Analytics Debug Information:')
    console.log(this.analyticsManager.getDebugInfo())
    
    if (this.analyticsManager.hasConsent()) {
      await this.analyticsManager.testConnection()
    } else {
      console.log('⚠️ Analytics consent not given. Grant consent first to test.')
    }
  }

  /**
   * Debug method to check asset loading status
   * Can be called from browser console: window.app.debugAssets()
   */
  debugAssets(): void {
    console.log('🔍 Asset Loading Debug Information:')
    console.log('Assets ready:', this.assetPreloader.isReady())
    console.log('Loading progress:', this.assetPreloader.getLoadingProgress() + '%')
    console.log('Asset details:', this.assetPreloader.getAllAssetInfo())
  }

  /**
   * Check browser compatibility for AR functionality
   * Requirements: 2.3, 2.4, 2.5 - Implement fallback behaviors for unsupported browsers
   */
  private async checkBrowserCompatibility(): Promise<BrowserCompatibilityResult> {
    const issues: string[] = []
    const warnings: string[] = []
    
    try {
      // Check HTTPS requirement
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        issues.push('HTTPS required for camera and location access')
      }
      
      // Check WebRTC support (camera access)
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        issues.push('Camera API not supported')
      }
      
      // Check Geolocation support
      if (!navigator.geolocation) {
        issues.push('Geolocation API not supported')
      }
      
      // Check WebGL support (required for AR)
      const webglSupported = this.checkWebGLSupport()
      if (!webglSupported) {
        issues.push('WebGL not supported - required for 3D AR content')
      }
      
      // Check A-Frame compatibility
      if (!window.AFRAME && !document.querySelector('script[src*="aframe"]')) {
        warnings.push('A-Frame library not detected')
      }
      
      // Check device capabilities
      const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      
      if (!isMobile && !hasTouch) {
        warnings.push('Desktop detected - AR experience optimized for mobile devices')
      }
      
      // Check browser version compatibility
      const browserInfo = this.getBrowserInfo()
      const browserCompatibility = this.checkBrowserVersionCompatibility(browserInfo)
      
      if (!browserCompatibility.isSupported) {
        issues.push(`Browser not supported: ${browserInfo.name} ${browserInfo.version}`)
      } else if (browserCompatibility.hasWarnings) {
        warnings.push(`Browser may have limited support: ${browserInfo.name} ${browserInfo.version}`)
      }
      
      return {
        isCompatible: issues.length === 0,
        issues,
        warnings,
        browserInfo
      }
      
    } catch (error) {
      console.error('Browser compatibility check failed:', error)
      return {
        isCompatible: false,
        issues: ['Failed to check browser compatibility'],
        warnings: [],
        browserInfo: { name: 'Unknown', version: 'Unknown' }
      }
    }
  }

  /**
   * Check WebGL support for AR functionality
   */
  private checkWebGLSupport(): boolean {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
      return !!gl
    } catch (error) {
      return false
    }
  }

  /**
   * Get browser information for compatibility checking
   */
  private getBrowserInfo(): { name: string; version: string } {
    const userAgent = navigator.userAgent
    let browserName = 'Unknown'
    let browserVersion = 'Unknown'

    if (userAgent.includes('CriOS')) {
      browserName = 'Chrome'
      const match = userAgent.match(/CriOS\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Chrome')) {
      browserName = 'Chrome'
      const match = userAgent.match(/Chrome\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Firefox')) {
      browserName = 'Firefox'
      const match = userAgent.match(/Firefox\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
      browserName = 'Safari'
      const match = userAgent.match(/Version\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Edg/')) {
      browserName = 'Edge'
      const match = userAgent.match(/Edg\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    } else if (userAgent.includes('Edge')) {
      browserName = 'Edge'
      const match = userAgent.match(/Edge\/(\d+)/)
      browserVersion = match ? match[1] : 'Unknown'
    }

    return { name: browserName, version: browserVersion }
  }

  /**
   * Check browser version compatibility
   */
  private checkBrowserVersionCompatibility(browserInfo: { name: string; version: string }): { isSupported: boolean; hasWarnings: boolean } {
    const version = parseInt(browserInfo.version)
    
    switch (browserInfo.name) {
      case 'Chrome':
        return {
          isSupported: version >= 70,
          hasWarnings: version < 80
        }
      case 'Firefox':
        return {
          isSupported: version >= 65,
          hasWarnings: version < 75
        }
      case 'Safari':
        return {
          isSupported: version >= 12,
          hasWarnings: version < 14
        }
      case 'Edge':
        return {
          isSupported: version >= 79,
          hasWarnings: version < 85
        }
      default:
        return {
          isSupported: false,
          hasWarnings: true
        }
    }
  }

  /**
   * Handle browser compatibility errors
   * Requirements: 2.3, 2.4, 2.5
   */
  private handleBrowserCompatibilityError(compatibilityResult: BrowserCompatibilityResult): void {
    console.error('Browser compatibility issues detected:', compatibilityResult.issues)
    
    // Fire analytics event for unsupported browser
    this.analyticsManager.fireEvent('browser_compatibility_error', {
      browser_name: compatibilityResult.browserInfo.name,
      browser_version: compatibilityResult.browserInfo.version,
      issues: compatibilityResult.issues,
      warnings: compatibilityResult.warnings
    }).catch(error => console.error('Failed to fire browser compatibility analytics:', error))
    
    // Update state and show error
    this.updateState({ 
      errorMessage: 'Browser not supported for AR experience',
      permissions: {
        camera: false,
        location: false
      }
    })
    
    this.transitionToState(AppStateType.ERROR)
    this.uiManager.showBrowserCompatibilityError(compatibilityResult)
  }

  /**
   * Handle camera permission errors
   * Requirements: 2.3, 2.4, 2.5
   */
  private async handleCameraPermissionError(error: any): Promise<void> {
    console.error('Camera permission error:', error)
    
    let errorType = ErrorType.CAMERA_PERMISSION_DENIED
    let errorMessage = 'Camera permission error'
    
    if (error instanceof Error) {
      switch (error.message) {
        case 'No camera device available':
          errorType = ErrorType.WEBRTC_NOT_SUPPORTED
          errorMessage = 'No camera device found'
          break
        case 'Camera is already in use':
          errorMessage = 'Camera is being used by another application'
          break
        case 'Camera requires secure context (HTTPS)':
          errorMessage = 'Secure connection required for camera access'
          break
        default:
          errorMessage = error.message
      }
    }
    
    // Fire analytics event
    await this.analyticsManager.fireCameraPermissionDenied()
    await this.analyticsManager.firePermissionsDenied()
    
    // Handle error
    this.handleError({
      type: errorType,
      message: errorMessage,
      timestamp: Date.now(),
      recoverable: true,
      details: { 
        originalError: error instanceof Error ? error.message : 'Unknown error',
        errorName: error instanceof Error ? error.name : 'Unknown'
      }
    })
    
    // Update state and show error screen
    this.updateState({ 
      permissions: {
        camera: false,
        location: false
      }
    })
    
    this.transitionToState(AppStateType.ERROR)
    this.uiManager.showCameraErrorScreen(errorMessage)
  }

  /**
   * Handle camera permission denied (user explicitly denied)
   * Requirements: 2.3, 2.4, 2.5
   */
  private async handleCameraPermissionDenied(): Promise<void> {
    // Fire analytics events
    await this.analyticsManager.fireCameraPermissionDenied()
    await this.analyticsManager.firePermissionsDenied()
    
    // Update state and show error
    this.updateState({ 
      permissions: {
        camera: false,
        location: false // Don't request location if camera failed
      }
    })
    
    this.transitionToState(AppStateType.ERROR)
    this.uiManager.showPermissionsErrorScreen()
  }

  /**
   * Handle location permission errors
   * Requirements: 2.3, 2.4, 2.5
   */
  private async handleLocationPermissionError(error: any): Promise<void> {
    console.error('Location permission error:', error)
    
    let errorMessage = 'Location permission error'
    
    if (error instanceof Error) {
      switch (error.message) {
        case 'Geolocation not supported':
          errorMessage = 'Location services not supported by this browser'
          break
        case 'Location service unavailable':
          errorMessage = 'Location services are currently unavailable'
          break
        case 'Location request timed out':
          errorMessage = 'Location request timed out - please try again'
          break
        default:
          errorMessage = error.message
      }
    }
    
    // Fire analytics events
    await this.analyticsManager.fireLocationPermissionDenied()
    await this.analyticsManager.firePermissionsDenied()
    
    // Handle error
    this.handleError({
      type: ErrorType.LOCATION_PERMISSION_DENIED,
      message: errorMessage,
      timestamp: Date.now(),
      recoverable: true,
      details: { 
        originalError: error instanceof Error ? error.message : 'Unknown error'
      }
    })
    
    // Update state and show error screen
    this.updateState({ 
      permissions: {
        camera: true, // Camera was granted
        location: false
      }
    })
    
    this.transitionToState(AppStateType.ERROR)
    this.uiManager.showPermissionsErrorScreen()
  }

  /**
   * Handle location permission denied (user explicitly denied)
   * Requirements: 2.3, 2.4, 2.5
   */
  private async handleLocationPermissionDenied(): Promise<void> {
    // Fire analytics events
    await this.analyticsManager.fireLocationPermissionDenied()
    await this.analyticsManager.firePermissionsDenied()
    
    // Update state and show error
    this.updateState({ 
      permissions: {
        camera: true, // Camera was granted
        location: false
      }
    })
    
    this.transitionToState(AppStateType.ERROR)
    this.uiManager.showPermissionsErrorScreen()
  }

  /**
   * Handle AR setup errors after permissions are granted
   * Requirements: 2.3, 2.4, 2.5
   */
  private handleARSetupError(error: any): void {
    console.error('AR setup error after permissions granted:', error)
    
    let errorType = ErrorType.AR_INITIALIZATION_FAILED
    let errorMessage = 'Failed to initialize AR experience'
    
    if (error instanceof Error) {
      if (error.message.includes('WebGL')) {
        errorType = ErrorType.WEBGL_NOT_SUPPORTED
        errorMessage = 'WebGL not supported - required for AR functionality'
      } else if (error.message.includes('MindAR')) {
        errorMessage = 'Failed to initialize AR tracking system'
      } else {
        errorMessage = error.message
      }
    }
    
    // Handle error
    this.handleError({
      type: errorType,
      message: errorMessage,
      timestamp: Date.now(),
      recoverable: true,
      details: { 
        originalError: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      }
    })
    
    // Show AR-specific error screen
    this.uiManager.showARInitializationError(errorMessage)
  }

  /**
   * Handle recording toggle
   * Phase 2: Recording feature
   */
  private async handleToggleRecording(_: boolean): Promise<void> {
    if (!this.arEngine) {
      console.warn('Cannot toggle recording - AR engine not initialized')
      return
    }

    try {
      const currentlyRecording = this.arEngine.isRecording()

      if (currentlyRecording) {
        // Stop recording
        console.log('handleToggleRecording: Stopping recording (toggle)')
        const result = await this.arEngine.stopRecording()
        if (result && result.success) {
          console.log('Recording stopped successfully')
        } else {
          console.warn('Recording stop returned failure or no result:', result)
        }
        document.dispatchEvent(new CustomEvent('recordingStateChanged', { detail: { isRecording: false } }))
        return
      }

      // Not currently recording -> require signed-in account before starting
      const authStatus = await getAuthAccountStatus()
      if (!authStatus.isSignedIn || !authStatus.hasAccount) {
        window.alert('You need to sign up or log in before using recording.')
        document.dispatchEvent(new CustomEvent('recordingStateChanged', { detail: { isRecording: false } }))
        return
      }

      // Not currently recording -> start
      console.log('handleToggleRecording: Starting recording (toggle)')
      const success = await this.arEngine.startRecording()
      if (success) {
        console.log('Recording started')
        document.dispatchEvent(new CustomEvent('recordingStateChanged', { detail: { isRecording: true } }))
      } else {
        console.error('Failed to start recording')
        this.takeScreenshot()
        document.dispatchEvent(new CustomEvent('recordingStateChanged', { detail: { isRecording: false } }))
      }
    } catch (error) {
      console.error('Error toggling recording:', error)
      document.dispatchEvent(new CustomEvent('recordingStateChanged', { detail: { isRecording: false } }))
    }
  }

  /**
   * Handle recording result
   * Phase 2: Recording feature - now shows in-app player
   */
  private handleRecordingResult(result: any): void {
    console.log('ApplicationController: handleRecordingResult called with:', {
      success: result?.success,
      hasUrl: !!result?.url,
      hasBlob: !!result?.blob,
      type: result?.type,
      format: result?.format
    })

    if (!result || !result.success) {
      console.warn('Recording result invalid or unsuccessful:', result)
      return
    }

    if (!result.url) {
      console.warn('Recording result missing URL:', result)
      return
    }

    console.log('ApplicationController: Showing video player with URL:', result.url)
    
    // Show in-app video player for replay
    try {
      this.uiManager.showVideoPlayer(result.url, result.blob)
      console.log('ApplicationController: Video player shown successfully')
    } catch (error) {
      console.error('ApplicationController: Error showing video player:', error)
    }
  }


  /**
   * Take screenshot as fallback
   * Phase 2: Recording feature
   */
  private takeScreenshot(): void {
    if (!this.arEngine) return

    const scene = this.arEngine.getScene()
    if (!scene) return

    const canvas = scene.querySelector('canvas') as HTMLCanvasElement
    if (!canvas) return

    try {
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = `rock-ar-screenshot-${Date.now()}.png`
          link.style.display = 'none'
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          console.log('Screenshot captured')
        }
      }, 'image/png')
    } catch (error) {
      console.error('Failed to take screenshot:', error)
    }
  }

  /**
   * Handle experience selection from Discover Panel
   * Phase 2: Discover Panel feature
   */
  private async handleExperienceSelected(experience: any): Promise<void> {
    console.log('Experience selected:', experience.id)
    
    // Fire analytics event
    await this.analyticsManager.fireEvent('experience_selected', {
      experience_id: experience.id,
      experience_title: experience.title,
      category: experience.category
    })

    // Show message to user (in production, would switch experiences)
    console.log(`Switching to experience: ${experience.title}`)
    // TODO: Implement experience switching logic
  }
}

/**
 * Browser compatibility check result
 */
interface BrowserCompatibilityResult {
  isCompatible: boolean
  issues: string[]
  warnings: string[]
  browserInfo: { name: string; version: string }
}